// src/pages/BookingManager.js
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../AuthContext";
import { API_BASE } from "../config/api";
import CountryPhoneInput from "./CountryPhoneInput";

const BASE = typeof API_BASE === "string" ? API_BASE : "";

/* ========================= Shared helpers (aligned with BillingForm) ========================= */

function toArray(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  if (data && Array.isArray(data.items)) return data.items;
  if (data && typeof data === "object") return Object.values(data);
  return [];
}

function normalizeLocalPhone(input) {
  if (!input) return "";
  return String(input).trim().replace(/[ \-()]/g, "");
}

// Round a Date to next 5-minute mark
function roundToNext5Min(d) {
  const copy = new Date(d);
  const ms = 1000 * 60 * 5;
  return new Date(Math.ceil(copy.getTime() / ms) * ms);
}

function formatDateLocalISO(d) {
  // yyyy-mm-dd in local time
  const dt = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return dt.toISOString().slice(0, 10);
}

function formatTimeLocalHM(d) {
  // HH:MM in local time
  const dt = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return dt.toISOString().slice(11, 16);
}

function addMinutesToTime(timeStr, minutes) {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":").map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return "";
  const total = h * 60 + m + minutes;
  const hh = Math.floor(total / 60) % 24;
  const mm = total % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

/* ========================= Component ========================= */

export default function BookingManager() {
  const { access, refreshAccessToken, logout } = useAuth() || {};

  /* ---------- authFetch (mirrors BillingForm pattern) ---------- */
  async function authFetch(url, options = {}) {
    const token = access || (await refreshAccessToken());
    if (!token) throw new Error("No auth token");

    const opts = { ...options };
    const headers = {
      ...(opts.headers || {}),
      Authorization: `Bearer ${token}`,
    };

    if (opts.body && typeof opts.body === "object" && !(opts.body instanceof FormData)) {
      headers["Content-Type"] = headers["Content-Type"] || "application/json";
      if (headers["Content-Type"] === "application/json") {
        opts.body = JSON.stringify(opts.body);
      }
    }

    return fetch(`${BASE}${url}`, { ...opts, headers });
  }

  async function fetchMaybeAuth(url) {
    try {
      const token = access || (await refreshAccessToken());
      if (token) {
        return await authFetch(url);
      }
    } catch {}
    return fetch(`${BASE}${url}`);
  }

  function handleAuthError() {
    if (typeof logout === "function") {
      logout();
      return;
    }
    ["access", "refresh", "token", "tokenData", "user", "auth"].forEach((k) =>
      localStorage.removeItem(k)
    );
    window.location.href = "/login";
  }

  /* ---------- View toggle (Create vs Calendar) ---------- */
  const [viewMode, setViewMode] = useState("create"); // "create" | "calendar"

  /* ---------- Customer block (mirrors BillingForm) ---------- */
  const [searchText, setSearchText] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [customerFormOpen, setCustomerFormOpen] = useState(false);
  const [customerFormMode, setCustomerFormMode] = useState("new"); // 'new' | 'edit'
  const [customerFormName, setCustomerFormName] = useState("");
  const [customerFormPhone, setCustomerFormPhone] = useState("");
  const [customerFormLanguage, setCustomerFormLanguage] = useState("EN");
  const [busy, setBusy] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState(null);

  async function onSearch() {
    const q = normalizeLocalPhone(searchText);
    if (!q || q.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const token = access || (await refreshAccessToken());
      if (!token) return;
      const res = await fetch(`${BASE}/api/search-customer/?query=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => []);
      setSearchResults(toArray(json));
    } finally {
      setSearching(false);
    }
  }

  function openQuickCreate() {
    const phoneGuess = normalizeLocalPhone(searchText);
    setCustomerFormMode("new");
    setCustomerFormName("");
    setCustomerFormPhone(phoneGuess || "");
    setCustomerFormLanguage("EN");
    setCustomerFormOpen(true);
  }

  function openEditCustomer() {
    if (!selectedCustomer) return;
    setCustomerFormMode("edit");
    setCustomerFormName(selectedCustomer.name || "");
    setCustomerFormPhone(selectedCustomer.phone || "");
    setCustomerFormLanguage(selectedCustomer.language || "EN");
    setCustomerFormOpen(true);
  }

  async function saveCustomerForm() {
    const name = customerFormName.trim();
    const phone = normalizeLocalPhone(customerFormPhone.trim());
    const language = customerFormLanguage;

    if (!phone) {
      alert("Phone number is required.");
      return;
    }
    if (!name) {
      alert("Name is required.");
      return;
    }

    setBusy(true);
    try {
      const token = access || (await refreshAccessToken());
      if (!token) return;

      if (customerFormMode === "new") {
        const res = await fetch(`${BASE}/api/create-customer/`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ phone, name, language }),
        });
        const js = await res.json().catch(() => ({}));
        if (!res.ok || !js.customer_id) {
          alert(js.error || "Failed to create customer");
          return;
        }
        const newCust = {
          id: js.customer_id,
          name: js.name || name,
          phone: js.phone || phone,
          language: js.language || language,
        };
        setSelectedCustomer(newCust);
        setSearchResults([]);
      } else if (customerFormMode === "edit" && selectedCustomer) {
        const res = await authFetch(`/api/update-customer/${selectedCustomer.id}/`, {
          method: "PATCH",
          body: { name, phone, language },
        });
        const js = await res.json().catch(() => ({}));
        if (!res.ok) {
          alert(js.error || "Failed to update customer");
          return;
        }
        const savedCustomer = js.customer || {};
        setSelectedCustomer((prev) => ({
          ...(prev || {}),
          name: savedCustomer.name || name,
          phone: savedCustomer.phone || phone,
          language: savedCustomer.language || language,
        }));
      }

      setCustomerFormOpen(false);
    } finally {
      setBusy(false);
    }
  }

  /* ---------- Services / catalogs / staff (mirrors BillingForm) ---------- */
  const [, setCatalogs] = useState([]);
  const [servicesByCatalog, setServicesByCatalog] = useState({});
  const [staff, setStaff] = useState([]);
  const [staffForService, setStaffForService] = useState([]);
  const catalogNames = useMemo(() => Object.keys(servicesByCatalog || {}), [servicesByCatalog]);

  // For the “add row” selector
  const [selCatalog, setSelCatalog] = useState("");
  const [selServiceId, setSelServiceId] = useState("");
  const [selServiceDuration, setSelServiceDuration] = useState(30);
  const [selServiceStaffId, setSelServiceStaffId] = useState("");

  const servicesInCatalog = useMemo(() => {
    const list = (servicesByCatalog && servicesByCatalog[selCatalog]) || [];
    return Array.isArray(list) ? list : [];
  }, [servicesByCatalog, selCatalog]);

  /* ---------- Base date/time defaults to now ---------- */
  const [baseDate, setBaseDate] = useState("");
  const [baseTime, setBaseTime] = useState("");
  useEffect(() => {
    // initialize once
    const now = roundToNext5Min(new Date());
    setBaseDate((prev) => prev || formatDateLocalISO(now));
    setBaseTime((prev) => prev || formatTimeLocalHM(now));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        // catalogs
        const cRes = await fetchMaybeAuth(`/api/catalogs/`);
        if (cRes.status === 401 || cRes.status === 403) {
          handleAuthError();
          return;
        }
        const cJson = await cRes.json().catch(() => []);
        setCatalogs(Array.isArray(cJson) ? cJson : []);

        // services (grouped by catalog; includes duration on each service)
        const sRes = await fetchMaybeAuth(`/api/services/`);
        if (sRes.status === 401 || sRes.status === 403) {
          handleAuthError();
          return;
        }
        const sJson = await sRes.json().catch(() => ({}));
        setServicesByCatalog(sJson && typeof sJson === "object" ? sJson : {});

        // staff
        const stRes = await authFetch(`/api/staff/`);
        if (stRes.status === 401 || stRes.status === 403) {
          handleAuthError();
          return;
        }
        const stJson = await stRes.json().catch(() => []);
        setStaff(
          toArray(stJson)
            .map((u) => ({
              id: u.id ?? u.pk ?? u.user_id ?? u.staff_id ?? u,
              name: u.display_name ?? u.username ?? u.name ?? u.full_name ?? `Staff ${u.id ?? ""}`,
            }))
            .filter((x) => x.id)
        );
      } catch (e) {
        console.error("Failed to load reference data", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selServiceId) {
      setStaffForService([]);
      return;
    }
    (async () => {
      try {
        const res = await authFetch(
          `/api/staff/by-service/${selServiceId}/?date=${encodeURIComponent(baseDate || "")}`
        );
        if (!res.ok) {
          setStaffForService([]);
          return;
        }
        const js = await res.json().catch(() => []);
        setStaffForService(toArray(js));
      } catch (e) {
        console.error("Failed to load staff for service", e);
        setStaffForService([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selServiceId, baseDate]);

  function onPickService(idStr) {
    setSelServiceId(idStr);
    const svc = servicesInCatalog.find((s) => String(s.id) === String(idStr));
    const duration = svc ? Number(svc.duration ?? 30) : 30;
    setSelServiceDuration(duration);
  }

  /* ---------- Appointment rows (chain times, compute end from duration) ---------- */
  const [serviceRows, setServiceRows] = useState([]); // {id, serviceId, staffId, date, startTime, endTime, duration}

  function addServiceRow() {
    if (!baseDate || !baseTime) {
      alert("Set base date & time first.");
      return;
    }

    const svc = servicesInCatalog.find((s) => String(s.id) === String(selServiceId));
    if (!svc) {
      alert("Pick a service");
      return;
    }
    const staffId = selServiceStaffId ? Number(selServiceStaffId) : null;
    if (!staffId) {
      alert("Pick a staff");
      return;
    }

    // start time = base for first row, else previous end
    const start =
      serviceRows.length === 0
        ? baseTime
        : serviceRows[serviceRows.length - 1].endTime || baseTime;
    const duration = Number(svc.duration ?? selServiceDuration ?? 30) || 30;
    const end = addMinutesToTime(start, duration);

    const newRow = {
      id: `row-${Date.now()}`,
      serviceId: Number(svc.id),
      staffId,
      date: baseDate,
      startTime: start,
      endTime: end,
      duration,
      name: svc.name,
      catalog: selCatalog || "",
    };

    setServiceRows((rows) => [...rows, newRow]);

    // reset pickers for next add (optional)
    // setSelServiceId(""); setSelServiceStaffId(""); // keep selections if preferred
  }

  function removeRow(id) {
    setServiceRows((rows) => rows.filter((r) => r.id !== id));
  }

  // if user edits start time of a row, recompute end based on stored duration for that row
  function changeRowStartTime(id, newStart) {
    setServiceRows((rows) =>
      rows.map((r) =>
        r.id === id
          ? { ...r, startTime: newStart, endTime: addMinutesToTime(newStart, r.duration) }
          : r
      )
    );
  }

  const totalMinutes = useMemo(
    () => serviceRows.reduce((sum, r) => sum + (Number(r.duration) || 0), 0),
    [serviceRows]
  );
  const visitEndTime = useMemo(
    () => (baseTime && totalMinutes ? addMinutesToTime(baseTime, totalMinutes) : ""),
    [baseTime, totalMinutes]
  );

  /* ---------- Submit appointment ---------- */
  const [creating, setCreating] = useState(false);
  const [status, setStatus] = useState("");

  async function submitAppointment(e) {
    e.preventDefault();
    setStatus("");

    if (!selectedCustomer) {
      alert("Select a customer.");
      return;
    }
    if (!baseDate || !baseTime) {
      alert("Select base date & time.");
      return;
    }
    if (serviceRows.length === 0) {
      alert("Add at least one service.");
      return;
    }

    const servicesPayload = serviceRows.map((r) => ({
      service_id: Number(r.serviceId),
      staff_id: Number(r.staffId),
      start_time: r.startTime, // backend uses base date + per-row start_time
    }));

    setCreating(true);
    try {
      const payload = {
        customer_id: selectedCustomer.id,
        date: baseDate,
        time: baseTime,
        services: servicesPayload,
      };
      const res = await authFetch(`/api/appointments/create/`, {
        method: "POST",
        body: payload,
      });

      if (!res.ok) {
        let msg = "Failed to create";
        try {
          const j = await res.json();
          if (j?.error) msg = j.error;
        } catch {}
        setStatus(msg);
        return;
      }

      setStatus("Appointment created ✅");
      // reset rows but keep customer in case they want to create another slot
      setServiceRows([]);
      // rebase time to now for convenience
      const now = roundToNext5Min(new Date());
      setBaseDate(formatDateLocalISO(now));
      setBaseTime(formatTimeLocalHM(now));
    } catch (e2) {
      console.error("create appointment failed", e2);
      setStatus("Failed to create");
    } finally {
      setCreating(false);
    }
  }

  /* ---------- Calendar view ---------- */
  const [calendarDate, setCalendarDate] = useState(formatDateLocalISO(new Date()));
  const [calendarData, setCalendarData] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState("");

  async function loadCalendar() {
    setCalendarError("");
    setCalendarLoading(true);
    try {
      const res = await authFetch(`/api/staff-calendar/?date=${encodeURIComponent(calendarDate)}`);
      if (!res.ok) {
        setCalendarError("Failed to load calendar");
        setCalendarData([]);
        return;
      }
      const js = await res.json().catch(() => []);
      setCalendarData(Array.isArray(js) ? js : []);
    } catch (err) {
      console.error("calendar load failed", err);
      setCalendarError("Failed to load calendar");
      setCalendarData([]);
    } finally {
      setCalendarLoading(false);
    }
  }

  useEffect(() => {
    if (viewMode === "calendar") loadCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, calendarDate]);

  /* ========================= RENDER ========================= */

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "1rem" }}>
      <h3 style={{ marginTop: 0 }}>Bookings</h3>

      <div style={{ marginBottom: 12, display: "flex", gap: 8 }}>
        <label>View:</label>
        <select value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
          <option value="create">Create / Edit Appointment</option>
          <option value="calendar">Staff Day View (Calendar)</option>
        </select>
      </div>

      {viewMode === "create" && (
        <>
          {/* Customer (mirrors BillingForm’s block) */}
          <div
            style={{
              border: "1px solid #eee",
              borderRadius: 10,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <h3 style={{ marginTop: 0 }}>Customer</h3>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search by name or phone"
                style={{ flex: "1 1 360px" }}
              />
              <button onClick={onSearch} disabled={searching}>
                {searching ? "Searching..." : "Search"}
              </button>
              <button onClick={openQuickCreate} disabled={busy}>
                Quick Create
              </button>
              <button
                onClick={() => {
                  setSelectedCustomer(null);
                  setSearchResults([]);
                }}
              >
                Clear
              </button>
            </div>

            {customerFormOpen && (
              <div
                style={{
                  marginTop: 10,
                  padding: 10,
                  borderRadius: 8,
                  border: "1px solid #ddd",
                  background: "#fafafa",
                }}
              >
                <div style={{ marginBottom: 6 }}>
                  <strong>{customerFormMode === "new" ? "Create New Customer" : "Edit Customer"}</strong>
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                  <input
                    type="text"
                    value={customerFormName}
                    onChange={(e) => setCustomerFormName(e.target.value)}
                    placeholder="Name"
                    style={{ flex: "1 1 200px" }}
                  />
                  <CountryPhoneInput value={customerFormPhone} onChange={setCustomerFormPhone} />
                  <select
                    value={customerFormLanguage}
                    onChange={(e) => setCustomerFormLanguage(e.target.value)}
                    style={{ flex: "1 1 200px" }}
                  >
                    <option value="EN">English</option>
                    <option value="AR">Arabic</option>
                    <option value="FR">French</option>
                  </select>
                  <button onClick={saveCustomerForm} disabled={busy}>
                    Save
                  </button>
                  <button type="button" onClick={() => setCustomerFormOpen(false)} disabled={busy}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {searchResults.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <select
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    const c = searchResults.find((x) => Number(x.id) === id);
                    if (!c) return;
                    setSelectedCustomer({
                      id: c.id,
                      name: c.name,
                      phone: c.phone,
                      language: c.language || null,
                    });
                    setSearchResults([]);
                  }}
                  defaultValue=""
                >
                  <option value="" disabled>
                    Pick a customer…
                  </option>
                  {searchResults.map((c) => (
                    <option key={c.id} value={c.id}>
                      {(c.name || "").trim() || "(no name)"} — {c.phone}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedCustomer && (
              <div
                style={{
                  marginTop: 8,
                  padding: 8,
                  background: "#fff5fb",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                }}
              >
                <div>
                  <strong>Selected:</strong> {selectedCustomer.name || "(no name)"} —{" "}
                  {selectedCustomer.phone}
                  {selectedCustomer.language ? ` [${selectedCustomer.language}]` : ""}
                </div>
                <button onClick={openEditCustomer} disabled={busy}>
                  Edit Customer
                </button>
              </div>
            )}
          </div>

          {/* Appointment Details */}
          <form onSubmit={submitAppointment}>
            <div
              style={{
                border: "1px solid #eee",
                borderRadius: 10,
                padding: 12,
                marginBottom: 12,
              }}
            >
              <h3 style={{ marginTop: 0 }}>Appointment Details</h3>

              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <label>
                    <strong>Base Date</strong>
                  </label>
                  <input
                    className="input"
                    type="date"
                    value={baseDate}
                    onChange={(e) => setBaseDate(e.target.value)}
                  />
                </div>

                <div>
                  <label>
                    <strong>Base Time</strong> (first service starts at)
                  </label>
                  <input
                    className="input"
                    type="time"
                    value={baseTime}
                    onChange={(e) => setBaseTime(e.target.value)}
                  />
                </div>

                {totalMinutes > 0 && (
                  <div style={{ alignSelf: "flex-end", marginTop: 22 }}>
                    <strong>Total visit:</strong> {totalMinutes} min
                    {visitEndTime && baseTime ? ` (from ${baseTime} to ${visitEndTime})` : ""}
                  </div>
                )}
              </div>
            </div>

            {/* Add Service (mirrors BillingForm selection but uses durations instead of price) */}
            <div
              style={{
                border: "1px solid #eee",
                borderRadius: 10,
                padding: 12,
                marginBottom: 12,
              }}
            >
              <h3 style={{ marginTop: 0 }}>Add Service</h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 2fr 1fr 1fr auto",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <select
                  value={selCatalog}
                  onChange={(e) => {
                    setSelCatalog(e.target.value);
                    setSelServiceId("");
                    setSelServiceDuration(30);
                    setSelServiceStaffId("");
                  }}
                >
                  <option value="">Select Category</option>
                  {catalogNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>

                <select
                  value={selServiceId}
                  onChange={(e) => onPickService(e.target.value)}
                  disabled={!selCatalog}
                >
                  <option value="">Select Service</option>
                  {servicesInCatalog.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>

                <select
                  value={selServiceStaffId}
                  onChange={(e) => setSelServiceStaffId(e.target.value)}
                >
                  <option value="">Choose Staff</option>
                  {(staffForService.length ? staffForService : staff).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  value={`${selServiceDuration} min`}
                  readOnly
                  title="Duration is defined by the service on the backend"
                />

                <button type="button" onClick={addServiceRow}>
                  Add
                </button>
              </div>
            </div>

            {/* Rows */}
            <div
              style={{
                border: "1px solid #eee",
                borderRadius: 10,
                padding: 12,
                marginBottom: 12,
              }}
            >
              <h3 style={{ marginTop: 0 }}>Selected Services</h3>
              {serviceRows.length === 0 ? (
                <div style={{ color: "#666" }}>No services yet. Add one above.</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", borderBottom: "1px solid #eee" }}>
                        Catalog
                      </th>
                      <th style={{ textAlign: "left", borderBottom: "1px solid #eee" }}>
                        Service
                      </th>
                      <th style={{ textAlign: "left", borderBottom: "1px solid #eee" }}>
                        Staff
                      </th>
                      <th style={{ textAlign: "left", borderBottom: "1px solid #eee" }}>
                        Date
                      </th>
                      <th style={{ textAlign: "left", borderBottom: "1px solid #eee" }}>
                        Start
                      </th>
                      <th style={{ textAlign: "left", borderBottom: "1px solid #eee" }}>
                        End
                      </th>
                      <th style={{ borderBottom: "1px solid #eee" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceRows.map((r) => (
                      <tr key={r.id}>
                        <td>{r.catalog || "-"}</td>
                        <td>{r.name}</td>
                        <td>
                          {(() => {
                            const s =
                              (staffForService.length ? staffForService : staff).find(
                                (x) => Number(x.id) === Number(r.staffId)
                              );
                            return s ? s.name : r.staffId;
                          })()}
                        </td>
                        <td>
                          <input
                            type="date"
                            value={r.date}
                            onChange={(e) =>
                              setServiceRows((rows) =>
                                rows.map((x) => (x.id === r.id ? { ...x, date: e.target.value } : x))
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="time"
                            value={r.startTime}
                            onChange={(e) => changeRowStartTime(r.id, e.target.value)}
                          />
                        </td>
                        <td>
                          <input type="time" value={r.endTime} readOnly title={`${r.duration} min`} />
                        </td>
                        <td>
                          <button type="button" onClick={() => removeRow(r.id)}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Actions */}
            <div
              style={{
                border: "1px solid #eee",
                borderRadius: 10,
                padding: 12,
              }}
            >
              <button
                type="submit"
                disabled={!selectedCustomer || !baseDate || !baseTime || serviceRows.length === 0 || creating}
              >
                {creating ? "Creating…" : "Create Appointment"}
              </button>
              {status && <div style={{ marginTop: 8 }}>{status}</div>}
            </div>
          </form>
        </>
      )}

      {viewMode === "calendar" && (
        <div
          style={{
            border: "1px solid #eee",
            borderRadius: 10,
            padding: 12,
          }}
        >
          <h4 style={{ marginTop: 0 }}>Staff Day View</h4>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
            <label>Date:</label>
            <input
              type="date"
              value={calendarDate}
              onChange={(e) => setCalendarDate(e.target.value)}
            />
            <button type="button" onClick={loadCalendar} disabled={calendarLoading}>
              Refresh
            </button>
          </div>

          {calendarLoading && <div>Loading calendar…</div>}
          {calendarError && <div style={{ color: "red", marginBottom: 8 }}>{calendarError}</div>}
          {!calendarLoading && calendarData.length === 0 && !calendarError && (
            <div>No bookings for this date.</div>
          )}

          {calendarData.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                overflowX: "auto",
                paddingTop: 8,
              }}
            >
              {calendarData.map((col) => (
                <div
                  key={col.staff_id}
                  style={{ minWidth: 220, border: "1px solid #ddd", borderRadius: 8, padding: 8 }}
                >
                  <div style={{ fontWeight: "bold", marginBottom: 6, textAlign: "center" }}>
                    {col.staff_name}
                  </div>
                  {col.slots.length === 0 ? (
                    <div style={{ fontSize: 12, opacity: 0.7 }}>No bookings</div>
                  ) : (
                    col.slots.map((slot) => (
                      <div
                        key={slot.appointment_id + "-" + slot.start}
                        style={{
                          border: "1px solid #eee",
                          borderRadius: 6,
                          padding: 6,
                          marginBottom: 6,
                          fontSize: 12,
                        }}
                      >
                        <div>
                          <strong>
                            {slot.start}–{slot.end}
                          </strong>
                        </div>
                        <div>{slot.customer_name}</div>
                        <div>{slot.service_name}</div>
                        <div style={{ opacity: 0.7 }}>{slot.status}</div>
                      </div>
                    ))
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
